import React from 'react';
import ModalButton from '../UI/ModalButton';
import LanguageSwitcher from '../UI/LanguageSwitcher';
import { useLanguage } from '../../context/LanguageContext';

interface PendingDialogProps {
  timeRemaining: number;
  onRefresh: () => void;
  onBackToMerchant?: () => void;
  refreshing?: boolean;
}

const PendingDialog: React.FC<PendingDialogProps> = ({
  timeRemaining,
  onRefresh,
  onBackToMerchant,
  refreshing = false
}) => {
  const { t } = useLanguage();

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m: ${secs.toString().padStart(2, '0')}s`;
  };

  const isDanger = timeRemaining <= 60;

  const ClockIcon = () => (
    <svg width="160" height="160" viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="80" cy="80" r="76" fill="#EFF6FF" stroke="#DBEAFE" strokeWidth="8"/>
      <circle cx="80" cy="80" r="56" fill="white" stroke="#3B82F6" strokeWidth="4"/>
      <path d="M80 48V80L100 100" stroke="#3B82F6" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="80" cy="80" r="6" fill="#3B82F6"/>
    </svg>
  );

  const TimerIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="13" r="8" stroke="#3B82F6" strokeWidth="2"/>
      <path d="M12 9V13L14 15" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round"/>
      <path d="M9 3H15" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round"/>
      <path d="M12 3V5" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  return (
    <div className="pending-dialog">
      <div className="dialog-header">
        <LanguageSwitcher />
      </div>
      <div className="pending-dialog__icon">
        <ClockIcon />
      </div>

      <div className="pending-dialog__content">
        <h2 className="pending-dialog__title">{t('pendingTitle')}</h2>
        <p className="pending-dialog__desc">
          {t('pendingDescription')}
          <strong>{t('pendingKeepOpen')}</strong>
        </p>
      </div>

      <div className={`pending-dialog__timer ${isDanger ? 'pending-dialog__timer--danger' : ''}`}>
        <TimerIcon />
        <span className="pending-dialog__timer-text">{t('waitingForTransfer')}</span>
        <span className="pending-dialog__timer-value">{formatTime(timeRemaining)}</span>
      </div>

      <div className="pending-dialog__actions">
        <ModalButton
          variant="primary"
          onClick={onRefresh}
          disabled={refreshing}
        >
          {refreshing ? t('processing') : t('refreshStatus')}
        </ModalButton>

        {onBackToMerchant && (
          <ModalButton
            variant="outline"
            onClick={onBackToMerchant}
          >
            {t('backToMerchant')}
          </ModalButton>
        )}
      </div>
    </div>
  );
};

export default PendingDialog;
